// L+TV Administration — demo mock data.
// NOTE: These are DEMO credentials and placeholder values only. When a backend
// (Readdy Backend or SaaS Supabase) is connected, replace all of this with
// real database records and secure server-side authentication.

export type AdminRole =
  | "SUPER ADMIN"
  | "ADMIN"
  | "EDITOR"
  | "JOURNALIST"
  | "VIDEO MANAGER"
  | "PROGRAM MANAGER"
  | "TECHNICAL ADMIN"
  | "SOCIAL MEDIA MANAGER"
  | "MODERATOR";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  department: string;
  status: "active" | "locked";
  initials: string;
}

export const adminUsers: AdminUser[] = [
  {
    id: "u-1",
    name: "L+TV Super Admin",
    email: "admin@ltv.tv",
    role: "SUPER ADMIN",
    department: "Management",
    status: "active",
    initials: "SA",
  },
  {
    id: "u-2",
    name: "Editorial Lead",
    email: "editor@ltv.tv",
    role: "EDITOR",
    department: "News Department",
    status: "active",
    initials: "EL",
  },
  {
    id: "u-3",
    name: "Field Journalist",
    email: "journalist@ltv.tv",
    role: "JOURNALIST",
    department: "News Department",
    status: "active",
    initials: "FJ",
  },
  {
    id: "u-4",
    name: "Video Manager",
    email: "video@ltv.tv",
    role: "VIDEO MANAGER",
    department: "Technical & Broadcasting",
    status: "active",
    initials: "VM",
  },
  {
    id: "u-5",
    name: "Broadcast Technician",
    email: "technical@ltv.tv",
    role: "TECHNICAL ADMIN",
    department: "Technical & Broadcasting",
    status: "active",
    initials: "BT",
  },
];

export interface DashboardStat {
  label: string;
  value: string;
  icon: string;
  iconBg: string;
  iconColor: string;
}

export const dashboardStats: DashboardStat[] = [
  { label: "Published News", value: "128", icon: "ri-newspaper-line", iconBg: "bg-primary-100", iconColor: "text-primary-700" },
  { label: "Draft News", value: "14", icon: "ri-draft-line", iconBg: "bg-secondary-100", iconColor: "text-secondary-700" },
  { label: "Pending Review", value: "7", icon: "ri-time-line", iconBg: "bg-accent-100", iconColor: "text-accent-700" },
  { label: "Published Videos", value: "86", icon: "ri-movie-line", iconBg: "bg-primary-100", iconColor: "text-primary-700" },
  { label: "Scheduled Programs", value: "22", icon: "ri-tv-line", iconBg: "bg-secondary-100", iconColor: "text-secondary-700" },
  { label: "Gallery Items", value: "310", icon: "ri-image-line", iconBg: "bg-accent-100", iconColor: "text-accent-700" },
  { label: "Newsletter Subscribers", value: "1,240", icon: "ri-mail-send-line", iconBg: "bg-primary-100", iconColor: "text-primary-700" },
  { label: "Contact Messages", value: "43", icon: "ri-message-line", iconBg: "bg-secondary-100", iconColor: "text-secondary-700" },
];

export interface LiveStatus {
  status: "live" | "offline" | "warning";
  label: string;
  sourceType: string;
  currentProgram: string;
  presenter: string;
  nextProgram: string;
}

export const liveStatus: LiveStatus = {
  status: "offline",
  label: "L+TV OFFLINE",
  sourceType: "Offline",
  currentProgram: "Preparing for Broadcast",
  presenter: "L+TV Broadcast Team",
  nextProgram: "L+TV News Hour at 18:00",
};

export interface ActivityItem {
  id: string;
  user: string;
  action: string;
  target: string;
  time: string;
  type: "news" | "video" | "live" | "program" | "settings" | "user";
}

export const recentActivity: ActivityItem[] = [
  { id: "a-1", user: "L+TV Super Admin", action: "published", target: "Edea 2026 Infrastructure Development Report", time: "10 min ago", type: "news" },
  { id: "a-2", user: "Video Manager", action: "uploaded", target: "L+TV Launch Ceremony — Full Coverage", time: "42 min ago", type: "video" },
  { id: "a-3", user: "Broadcast Technician", action: "changed live source to", target: "Offline", time: "1 hr ago", type: "live" },
  { id: "a-4", user: "Editorial Lead", action: "approved", target: "Technology Summit Douala — Highlights", time: "2 hrs ago", type: "news" },
  { id: "a-5", user: "L+TV Super Admin", action: "updated", target: "Website Settings — Social Links", time: "3 hrs ago", type: "settings" },
  { id: "a-6", user: "Program Manager", action: "scheduled", target: "Sports Central — Thursday 19:30", time: "5 hrs ago", type: "program" },
];

export interface PendingTask {
  id: string;
  title: string;
  type: string;
  due: string;
  priority: "high" | "medium" | "low";
}

export const pendingTasks: PendingTask[] = [
  { id: "t-1", title: "Review 7 pending articles awaiting approval", type: "Newsroom", due: "Today", priority: "high" },
  { id: "t-2", title: "Configure direct HLS stream source", type: "Live TV", due: "Today", priority: "high" },
  { id: "t-3", title: "Upload thumbnails for 3 new videos", type: "Videos", due: "Tomorrow", priority: "medium" },
  { id: "t-4", title: "Schedule Sunday Faith & Community program", type: "TV Schedule", due: "This week", priority: "medium" },
  { id: "t-5", title: "Respond to 4 advertising enquiries", type: "Advertising", due: "This week", priority: "low" },
];

export interface AdminNotification {
  id: string;
  type: "message" | "article" | "live" | "application" | "event" | "security";
  title: string;
  message: string;
  time: string;
  read: boolean;
}

export const adminNotifications: AdminNotification[] = [
  { id: "n-1", type: "article", title: "Pending article review", message: "3 articles are awaiting your approval in the Newsroom.", time: "10 min ago", read: false },
  { id: "n-2", type: "live", title: "Live stream is offline", message: "The current live source is set to Offline. Configure a stream when ready.", time: "1 hr ago", read: false },
  { id: "n-3", type: "message", title: "New contact message", message: "A new enquiry arrived in the Advertising inbox.", time: "2 hrs ago", read: false },
  { id: "n-4", type: "application", title: "New career application", message: "A candidate applied for the 'Video Editor' position.", time: "5 hrs ago", read: true },
  { id: "n-5", type: "event", title: "Scheduled publication", message: "A news article is scheduled to publish tomorrow at 09:00.", time: "Yesterday", read: true },
];

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  department: string;
  subject: string;
  message: string;
  time: string;
  read: boolean;
}

export const contactMessages: ContactMessage[] = [
  { id: "m-1", name: "Alain Mbarga", email: "alain.mbarga@example.com", department: "Advertising", subject: "TV ad campaign enquiry", message: "Hello, I would like to enquire about advertising my business on L+TV during prime time. Please share available options.", time: "2 hrs ago", read: false },
  { id: "m-2", name: "Rosine Etoa", email: "rosine.etoa@example.com", department: "Newsroom", subject: "Story tip — community event", message: "There is a community health event happening in Edea this weekend that may be worth covering.", time: "5 hrs ago", read: false },
  { id: "m-3", name: "David Fotso", email: "david.fotso@example.com", department: "Partnerships", subject: "Collaboration proposal", message: "Our organization is interested in a media partnership with L+TV. Could we schedule a call?", time: "Yesterday", read: true },
];